<template>
  <div :class="$style.container">
    <MyPropertiesHeroSection />
    <div
      v-for="section in myPropertiesSections"
      :key="`${section.action}-${section.label}`"
      :ref="(element) => bindSectionRef(element as HTMLDivElement | null, section.elRef)"
    >
      <component :is="section.component" />
    </div>
  </div>
</template>

<script setup lang="ts">
import MyPropertiesCredibilitySection from '@/components/my-properties-landing-page/MyPropertiesCredibilitySection/MyPropertiesCredibilitySection.vue';
import MyPropertiesHeroSection from '@/components/my-properties-landing-page/MyPropertiesHeroSection/MyPropertiesHeroSection.vue';
import MyPropertiesHowItWorksSection from '@/components/my-properties-landing-page/MyPropertiesHowItWorksSection/MyPropertiesHowItWorksSection.vue';
import MyPropertiesMarketTrendsSection from '@/components/my-properties-landing-page/MyPropertiesMarketTrendsSection/MyPropertiesMarketTrendsSection.vue';
import MyPropertiesNextStepsSection from '@/components/my-properties-landing-page/MyPropertiesNextStepsSection/MyPropertiesNextStepsSection.vue';
import MyPropertiesPriceEstimationSection from '@/components/my-properties-landing-page/MyPropertiesPriceEstimationSection/MyPropertiesPriceEstimationSection.vue';
import MyPropertiesPropertyOverviewTeaserSection from '@/components/my-properties-landing-page/MyPropertiesPropertyOverviewTeaserSection/MyPropertiesPropertyOverviewTeaserSection.vue';
import MyPropertiesRenovationPlannerSection from '@/components/my-properties-landing-page/MyPropertiesRenovationPlannerSection/MyPropertiesRenovationPlannerSection.vue';
import MyPropertiesSimilarsSection from '@/components/my-properties-landing-page/MyPropertiesSimilarsSection/MyPropertiesSimilarsSection.vue';
import { useAuth0Client } from '@/composables/useAuth0Client';
import { useTrackOnVisible } from '@/composables/useTrackOnVisible';
import { getClientLoginUserId, refreshAuthProfileAndSyncStore } from '@/helpers/Auth/authClient';

import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { hasHowProperties } from '@/tracking/howPropertiesTracking';

import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';
import { useHead } from '@unhead/vue';
import { computed, markRaw, onMounted, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const { t, locale } = useI18n();

const headConfig = computed(() => ({
  title: t('myProperties.landing.meta.title'),
  htmlAttrs: {
    lang: locale.value,
  },
  meta: [
    {
      name: 'description',
      content: t('myProperties.landing.meta.description'),
    },
  ],
}));

useHead(headConfig);

const auth0 = useAuth0Client();
const store = useStore();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();

onMounted(async () => {
  const isUserLoggedIn = auth0?.isAuthenticated.value ?? false;

  if (isUserLoggedIn && auth0) {
    await refreshAuthProfileAndSyncStore(store, auth0);
  }

  const userHasProperties = isUserLoggedIn ? await hasHowProperties(store) : undefined;
  const clientLoginUserId = isUserLoggedIn ? getClientLoginUserId(store, auth0) : undefined;

  trackVirtualPageViewWithTreatment({
    subType: 'landingpage_homeowner',
    locale: locale.value,
    stepInputs: undefined,
    moreParams: {
      h_login_status_035: isUserLoggedIn ? 'loggedin' : 'loggedout',
      ...(isUserLoggedIn && {
        h_user_group_039: userHasProperties ? 'is_how_user' : 'not_how_user',
        ...(clientLoginUserId && { h_client_login_user_id_036: clientLoginUserId }),
      }),
    },
  });
});

const bindSectionRef = (element: HTMLDivElement | null, elRef: Ref<HTMLElement | null>) => {
  elRef.value = element;
};

const myPropertiesSections = [
  {
    component: markRaw(MyPropertiesMarketTrendsSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.PROPERTY_PRICE_TREND_TEASER,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesHowItWorksSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.HOW_IT_WORKS,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesCredibilitySection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.CREDIBILITY,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesPriceEstimationSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.PROPERTY_RENTAL_PRICE_TEASER,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesPropertyOverviewTeaserSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.PROPERTY_OVERVIEW_TEASER,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesSimilarsSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.PROPERTY_SIMILAR_OFFERS_TEASER,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesRenovationPlannerSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.RENOVATION_PLANNER_TEASER,
    elRef: ref<HTMLElement | null>(null),
  },
  {
    component: markRaw(MyPropertiesNextStepsSection),
    action: TRACKING_ACTION.DISPLAY_HOMEOWNER_TEASER,
    label: TRACKING_LABEL.NEXT_STEPS_TEASER,
    elRef: ref<HTMLElement | null>(null),
  },
];

myPropertiesSections.forEach(({ elRef, action, label }) => {
  if (label === TRACKING_LABEL.RENOVATION_PLANNER_TEASER || label === TRACKING_LABEL.CREDIBILITY) {
    return;
  }

  useTrackOnVisible(elRef, action, label);
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.landingWrapper {
  position: relative;
  overflow: visible;
  padding-bottom: $space-800;
  display: flex;
  flex-direction: column;
}

.container {
  color: $color-grey-120;
  padding: $space-150 $space-100;

  @include grid-media-query(sm) {
    padding: $space-150 $space-200;
  }

  @include grid-media-query(md) {
    padding: $space-150 $space-200;
  }
}
</style>
